import type { FC } from 'react'

import type { WhiteLabel } from '@skytech/types'
import DOMPurify from 'dompurify'
import OpenInNewIcon from 'mdi-react/OpenInNewIcon'
import { twMerge } from 'tailwind-merge'

import type { DomPurifyOptions } from './types'

export type ExternalLinkSize = 'regular' | 'large' | 'semi-large'
type ExternalLinkColorMode = 'gray'

import './ExternalLink.styles.scss'

// Allow only safe protocols
const isSafeURL = (url: string): boolean => {
  if (typeof window === 'undefined') return false
  try {
    const parsed = new URL(url, window.location.origin)
    const allowedProtocols = ['https:', 'http:', 'mailto:', 'tel:']
    return (
      allowedProtocols.includes(parsed.protocol) &&
      !parsed.href.includes('javascript:') &&
      !parsed.href.includes('data:')
    )
  } catch {
    return false
  }
}

const sanitizeAttribute = (value: string | undefined, options: DomPurifyOptions = {}): string => {
  if (!value || typeof value !== 'string') return value ?? ''
  return DOMPurify.sanitize(value, {
    ALLOWED_TAGS: [], // No tags, only attributes
    ALLOWED_ATTR: ['href', 'title', ...(options.ALLOWED_ATTR || [])],
    ...options,
  })
}

// Sanitize children (if they contain HTML)
const sanitizeContent = (value: string | undefined, options: DomPurifyOptions = {}): string => {
  if (!value || typeof value !== 'string') return value ?? ''
  return DOMPurify.sanitize(value, {
    ALLOWED_TAGS: ['b', 'i', 'strong', 'em', ...(options.ALLOWED_TAGS || [])],
    ALLOWED_ATTR: [],
    ...options,
  })
}

export const ExternalLink: FC<
  {
    size?: ExternalLinkSize
    linkColorMode?: ExternalLinkColorMode
    hasUnderline?: boolean
    isWhiteLabeled?: boolean
    href: string
    children?: React.ReactNode
    title?: string
    fallbackHref?: string
    className?: string
    withIcon?: boolean
    onClick?: React.MouseEventHandler<HTMLAnchorElement>
    domPurifyOptions?: DomPurifyOptions
  } & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'title' | 'onClick'>
> = ({
  href,
  children,
  size = 'regular',
  linkColorMode = '',
  className = '',
  onClick,
  title,
  fallbackHref = '#',
  domPurifyOptions = {},
  hasUnderline = true,
  withIcon = false,
  isWhiteLabeled = false,
}) => {
  // todo: replace with the org from url when ready
  const whiteLabel = localStorage.getItem('org') as WhiteLabel
  const underlineClassName = hasUnderline ? 'underline' : ''

  const sanitizedHref = sanitizeAttribute(href, domPurifyOptions)
  const safeHref = isSafeURL(sanitizedHref) ? sanitizedHref : fallbackHref
  const sanitizedTitle = sanitizeAttribute(title, { ALLOWED_TAGS: [] })
  const safeChildren =
    typeof children === 'string' ? sanitizeContent(children, domPurifyOptions) : children
  const safeOnClick = typeof onClick === 'function' ? onClick : undefined

  return (
    <a
      target="_blank"
      rel="noopener noreferrer"
      href={safeHref}
      title={sanitizedTitle || undefined}
      onClick={safeOnClick}
      // todo: add the disabled mode
      className={twMerge(
        'ui-link',
        isWhiteLabeled && whiteLabel,
        size,
        linkColorMode,
        underlineClassName,
        className
      )}
    >
      {safeChildren}
      {withIcon && <OpenInNewIcon className="ml-1" />}
    </a>
  )
}
